import { motion } from 'framer-motion';
import PrimaryButton from '../global/PrimaryButton';

export default function DMVHero() {
  const dmvApp = {
    name: "DMV Practice Test",
    appStoreUrl: "https://apps.apple.com/us/app/dmv-test-practice/id6443748977",
    icon: "/apps/dmv-icon.webp",
    screenshots: [
      "/apps/dmv-screenshot-1.webp",
      "/apps/dmv-screenshot-2.webp",
      "/apps/dmv-screenshot-3.webp",
      "/apps/dmv-screenshot-4.webp"
    ]
  };

  return (
    <>
      {/* HERO – cinematic */}
      <section className="relative overflow-hidden py-12 lg:py-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-[100px] opacity-30" style={{ background: 'radial-gradient(ellipse at center, #0ea5e9 0%, #06b6d4 40%, transparent 70%)' }} />
        </div>
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white/60 tracking-widest text-[11px] font-medium">EDUCATION • MOST DOWNLOADED</span>
              </div>
              <div className="flex items-center justify-center lg:justify-start mt-6 mb-4 gap-4">
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl blur-xl opacity-50 bg-gradient-to-br from-sky-500 to-blue-600" />
                  <img src={dmvApp.icon} alt="DMV Practice Test App Icon" className="relative w-16 h-16 rounded-2xl border border-white/10 shadow-lg" />
                </div>
                <div className="text-left">
                  <h1 className="text-[32px] lg:text-[40px] font-semibold tracking-[-0.03em] leading-none text-white">DMV Practice Test</h1>
                  <p className="text-sm text-white/50 mt-1">The best way to pass your DMV test</p>
                </div>
              </div>
              <p className="text-[17px] leading-7 text-white/55 max-w-xl mx-auto lg:mx-0">
                Get ready for your DMV test with our comprehensive practice app. Study anytime, anywhere and pass with confidence.
              </p>
              <div className="flex flex-col sm:flex-row sm:justify-start justify-center items-center gap-4 mt-8">
                <a href={dmvApp.appStoreUrl} target="_blank" rel="noopener noreferrer" className="inline-block hover:scale-[1.02] transition">
                  <img src="/apps/app-store-badge.svg" alt="Download on the App Store" className="h-14 w-auto" />
                </a>
                <a href="#features"><PrimaryButton>Learn More</PrimaryButton></a>
              </div>
              <div className="mt-6 flex items-center justify-center lg:justify-start gap-3 text-xs text-white/40">
                <span>★★★★★</span><span className="text-white/60">4.8 on App Store</span><span className="h-3 w-px bg-white/10" /><span>Thousands of learners</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
              <div className="absolute -inset-4 rounded-[32px] blur-2xl opacity-30 bg-gradient-to-br from-sky-500/30 to-cyan-500/30" />
              <div className="relative glass-strong rounded-[28px] p-4 lg:p-6 gradient-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs tracking-widest text-white/40 font-medium">PREVIEW</div>
                  <span className="rounded-full bg-emerald-500/15 border border-emerald-500/20 text-emerald-300 text-xs px-3 py-1 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> App Store Live</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {dmvApp.screenshots.map((src, idx) => (
                    <img key={idx} src={src} alt={`DMV Practice Test screenshot ${idx + 1}`} className="rounded-2xl border border-white/10 bg-black/30 shadow-lg" loading="lazy" />
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 lg:left-6 lg:translate-x-0 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">✓</div>
                <div><div className="text-xs font-semibold text-white">Pass with confidence</div><div className="text-[11px] text-white/50">Updated for 2026 DMV</div></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="mx-auto max-w-[1280px] px-6 lg:px-8 py-16 lg:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] text-sky-300 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> WHY IT WORKS</div>
          <h2 className="mt-3 text-[32px] lg:text-[42px] font-semibold tracking-[-0.03em] leading-[0.95] text-white">Everything you need to pass</h2>
          <p className="mt-3 text-white/50">Practice with realistic questions, study traffic signs, and build confidence before test day.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {[
            { title: 'Traffic Signs & Road Rules', desc: 'Learn the rules of the road with up-to-date DMV questions and sign charts.', icon: '⬔', grad: 'from-sky-500 to-blue-600' },
            { title: 'Parking & Right of Way', desc: 'Understand parking laws, right-of-way rules, and safe driving etiquette.', icon: '◈', grad: 'from-cyan-500 to-blue-600' },
            { title: 'Defensive Driving', desc: 'Build habits that keep you and your passengers safe in every situation.', icon: '◆', grad: 'from-sky-600 to-blue-600' },
            { title: 'Night & Weather Driving', desc: 'Master safe driving tips for rain, snow, and low visibility.', icon: '⬢', grad: 'from-emerald-500 to-teal-600' },
            { title: 'Study on the Go Anywhere', desc: 'Access practice sessions anytime, from your phone or tablet.', icon: '◎', grad: 'from-orange-500 to-red-600' },
            { title: 'Pass Your DMV Test Faster', desc: 'Focused practice helps you feel ready and confident on test day.', icon: '⬣', grad: 'from-blue-600 to-cyan-500' },
          ].map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} whileHover={{ y: -4 }} className="group relative rounded-[24px] glass p-6 overflow-hidden hover:bg-white/[0.08] transition">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500" style={{ background: 'radial-gradient(400px circle at 50% 0%, rgba(14,165,233,0.12), transparent 70%)' }} />
              <div className={`relative w-10 h-10 rounded-xl bg-gradient-to-br ${f.grad} flex items-center justify-center text-white shadow-lg`}>{f.icon}</div>
              <h3 className="relative mt-5 text-[16px] font-semibold text-white">{f.title}</h3>
              <p className="relative mt-2 text-[13.5px] leading-6 text-white/50">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-[1280px] px-6 lg:px-8 pb-16">
        <div className="rounded-[32px] glass overflow-hidden p-6 lg:p-10">
          <div className="text-center mb-8">
            <h2 className="text-[24px] lg:text-[28px] font-semibold tracking-tight text-white">What learners are saying</h2>
            <p className="text-white/50 text-sm mt-2">Real feedback from people who used the app to pass their DMV test.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { quote: "This app made studying simple and stress-free. Passed first try!", name: "Sarah M.", location: "California", stars: "★★★★★" },
              { quote: "Clear explanations and easy practice. Loved the interface.", name: "Mike R.", location: "Texas", stars: "★★★★★" },
              { quote: "Short lessons that fit into my schedule. Highly recommend.", name: "Jessica L.", location: "New York", stars: "★★★★★" },
            ].map((t, i) => (
              <div key={i} className="rounded-[20px] bg-white text-black p-6 flex flex-col">
                <div className="text-amber-500 text-sm">{t.stars}</div>
                <p className="mt-3 text-[15px] leading-6 font-medium">“{t.quote}”</p>
                <div className="mt-4 flex items-center gap-3">
                  <img src={`https://i.pravatar.cc/100?img=${20 + i * 5}`} alt="" className="w-8 h-8 rounded-full" />
                  <div><div className="text-sm font-semibold">{t.name}</div><div className="text-xs text-black/50">{t.location}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1280px] px-6 lg:px-8 pb-20">
        <div className="relative rounded-[32px] overflow-hidden p-[1px] bg-gradient-to-br from-sky-500/40 via-sky-500/20 to-cyan-500/50">
          <div className="relative rounded-[31px] overflow-hidden bg-[#0a0a14] px-6 lg:px-10 py-12 text-center">
            <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(600px circle at 80% 20%, rgba(14,165,233,0.28), transparent 60%), radial-gradient(500px circle at 10% 80%, rgba(6,182,214,0.25), transparent 60%)' }} />
            <div className="relative">
              <h2 className="text-[28px] lg:text-[36px] font-semibold tracking-tight text-white">Ready to Pass Your DMV Test?</h2>
              <p className="text-white/55 mt-3 max-w-2xl mx-auto">Download now and skyrocket your chances of passing the Driver&apos;s License permit test. Join thousands who have already passed.</p>
              <div className="flex justify-center mt-7">
                <a href={dmvApp.appStoreUrl} target="_blank" rel="noopener noreferrer" className="hover:scale-[1.02] transition">
                  <img src="/apps/app-store-badge.svg" alt="Download on the App Store" className="h-14 w-auto" />
                </a>
              </div>
              <p className="text-xs text-white/30 mt-4">Updated for 2026 • Free on iPhone & iPad</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
